/**
 * useOperationalForms — Unified hook replacing useDataValidation + useDocumentGeneration
 * 
 * Reads form_configurations (form_type = 'operational') and manages form_submissions.
 * Single source for compliance forms (Electrical CoC, Gas CoC) and operational
 * documents (site surveys, service reports, job cards, waybills, PODs).
 * 
 * Architecture:
 * 1. Fetches operational forms from /api/v1/forms/operational matching work item type/subtype
 * 2. Loads any existing submissions for the work item from form_submissions
 * 3. Provides submit handler that saves to form_submissions and optionally generates PDF
 * 4. Exposes canCloseJob gate (all required forms must be submitted and passing)
 */

'use client';

import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { authService } from '@/lib/authService';
import { getApiUrl } from '@/lib/apiConfig';
import { isSilentFetchError } from '@/lib/capacitorFetchPatch';
import { OPERATIONS_SYNC_PENDING_CHANGED_EVENT } from '@/lib/operations/offlineSyncEvents';
import {
  cacheOfflineFormConfig,
  getQueuedOfflineFormSubmissions,
  listCachedOfflineFormConfigs,
} from '@/lib/offline/operationalFormsStore';
import { getIncompleteRequiredOperationalForms } from '../utils/operationalForms';
import { createLogger } from '@/lib/debugLogger';

const logger = createLogger('useOperationalForms');

/** Narrows an unknown value to a number, or undefined when not numeric. */
function toNumberOrUndefined(value: unknown): number | undefined {
  return typeof value === 'number' ? value : undefined;
}

// ============================================================================
// TYPES
// ============================================================================

export interface FormField {
  key: string;
  type: string;
  label: string;
  required?: boolean;
  is_required?: boolean;
  placeholder?: string;
  options?: unknown;
  validation?: Record<string, unknown>;
  sort_order: number;
  width?: string;
  styling?: Record<string, unknown>;
  description?: string;
  help_text?: string;
  metadata?: Record<string, unknown>;
  readonly?: boolean;
  is_readonly?: boolean;
}

export interface FormSection {
  key: string;
  title: string;
  description?: string;
  sort_order: number;
  is_required?: boolean;
  is_collapsible?: boolean;
  default_expanded?: boolean;
  icon?: string;
  validation_mode?: 'all' | 'any' | 'none';
  is_repeatable?: boolean;
  min_instances?: number;
  min_repeats?: number;
  max_instances?: number;
  max_repeats?: number;
  instance_label?: string;
  repeat_label_template?: string;
  repeat_group_key?: string;
  repeat_mode?: 'template' | 'fixed';
  fixed_instances?: number;
  lock_instance_count?: boolean;
  allow_add?: boolean;
  allow_remove?: boolean;
  allow_duplicate?: boolean;
  fields: FormField[];
}

export interface OperationalForm {
  id: number;
  form_key: string;
  entity_type: string;
  title: string;
  description?: string;
  form_schema: {
    sections: FormSection[];
    computed_fields?: unknown[];
    rules?: unknown[];
    behavior?: Record<string, unknown>;
    layout?: Record<string, unknown>;
    cover_page?: Record<string, unknown>;
    submit_button_text?: string;
  };
  form_type: string;
  category?: string;
  applies_to_item_type?: string;
  applies_to_subtype?: string;
  icon?: string;
  color?: string;
  is_system: boolean;
  enabled: boolean;
  sort_order: number;
  compliance_meta?: {
    regulatory_body?: string;
    certificate_validity_days?: number;
    require_all_pass?: boolean;
    pdf_template?: string;
    requires_professional_registration?: boolean;
    repeat_group_catalog?: Record<string, {
      label?: string;
      description?: string;
      min_instances?: number;
      max_instances?: number;
      section_keys?: string[];
    }>;
    generated_variant?: {
      source_form_id?: number;
      generated_for_work_item_id?: number | string;
      generated_from_submission_id?: number | string;
      repeat_group_counts?: Record<string, number>;
    };
    /** When set, allows technicians to edit this form for N days after
     *  their appointment ends (or until the parent job is closed). */
    editable_window_days?: number | null;
  };
  required_for_job?: boolean;
  required_for_appointment?: boolean;
  source_form_id?: number | null;
  assignment_scope?: 'template' | 'client_clone' | 'work_item_once_off';
  generated_for_work_item_id?: string | number | null;
  generated_from_submission_id?: string | number | null;
  assignment_source?: 'auto' | 'extra' | 'inherited' | 'submission';
  assignment_source_work_item_id?: string | number | null;
  pdf_template_key?: string;
  tags?: string[];
  version: number;
  /** Form theme config (accent color, bg color, header image, etc.) */
  theme_config?: Record<string, unknown> | null;
  /** Form branding config (logo URL, watermark, show branding, etc.) */
  branding_config?: Record<string, unknown> | null;
  /** Success page message shown after submission. */
  success_message?: string | null;
  /** Optional redirect URL after submission. */
  success_redirect_url?: string | null;
  /** Optional image to display on the success screen. */
  success_image_url?: string | null;
  /** Delay in seconds before auto-redirecting (0 = no redirect). */
  success_redirect_delay?: number | null;
  /** Whether to show a summary of submitted data on the success screen. */
  success_show_summary?: boolean | null;
  /** Whether the respondent may submit another response. */
  success_allow_another?: boolean | null;
  /** Whether to show the progress bar / ring. */
  show_progress_bar?: boolean | null;
}

export interface FormSubmission {
  id: number;
  form_configuration_id: number;
  form_key: string;
  work_item_id?: number | string;
  entity_type: string;
  entity_id?: number | string;
  client_id: number;
  submitted_by: number;
  form_data: Record<string, unknown>;
  section_results?: Array<{
    section_key: string;
    status: 'pass' | 'fail' | 'na' | 'pending';
    fields_completed: number;
    fields_total: number;
  }>;
  overall_status: 'draft' | 'pass' | 'fail' | 'conditional' | 'pending';
  completion_percentage: number;
  pdf_url?: string;
  signature_data?: Record<string, unknown>;
  gps_data?: { lat: number; lng: number; accuracy: number };
  submitted_at?: string;
  created_at: string;
  updated_at: string;
  local_only?: boolean;
  offline_source?: 'queue';
  local_submission_id?: string;
}

// ============================================================================
// WORK ITEM TYPE (minimal interface for this hook)
// ============================================================================

export interface WorkItem {
  id: number | string;
  item_type?: string;
  item_subtype?: string;
  item_number?: string;
  parent_id?: number | string;
  parent_job_number?: string;
  metadata?: Record<string, unknown>;
  custom_fields?: Record<string, unknown>;
  title?: string;
  description?: string;
  contact_name?: string;
  contact_phone?: string;
  contact_email?: string;
  contact_person?: string;
  contact_person_id?: number;
  customer_name?: string;
  customer_phone?: string;
  customer_email?: string;
  location_id?: number;
  location_address?: string;
  location_lat?: number;
  location_lng?: number;
  location_name?: string;
  location?: string;
  location_coordinates?: string | { lat?: number; lng?: number; x?: number; y?: number } | null;
  cl_location_name?: string;
  cl_address?: string;
  cl_contact_person?: string;
  cl_contact_phone?: string;
  cl_city?: string;
  priority?: string;
  status?: string;
  scheduled_start?: string;
  scheduled_end?: string;
  due_date?: string;
}

/**
 * Parse a work-item location_coordinates value ({lat,lng} | {x,y} | "lat,lng")
 * — mirrors parseWorkItemPoint in /api/v1/work-items/[id] (x→lat, first→lat).
 */
function parseWorkItemCoordinates(
  value: WorkItem['location_coordinates'],
): { lat?: number; lng?: number } {
  if (!value) return {};
  if (typeof value === 'object') {
    const lat = Number(value.x ?? value.lat);
    const lng = Number(value.y ?? value.lng);
    return {
      lat: Number.isFinite(lat) ? lat : undefined,
      lng: Number.isFinite(lng) ? lng : undefined,
    };
  }
  const match = /\(?\{?([^,]+),([^)\}]+)\)?\}?/.exec(String(value));
  if (!match) return {};
  const lat = Number(match[1]);
  const lng = Number(match[2]);
  return {
    lat: Number.isFinite(lat) ? lat : undefined,
    lng: Number.isFinite(lng) ? lng : undefined,
  };
}

/**
 * Field keys that resolve to system-acquired work-item/user/date context.
 * Keep in sync with the directMappings literal in buildWorkItemPrefillValues.
 * OperationalFormModal enforces readonly on these keys when a prefilled value
 * exists — acquired values must not be edited by the respondent.
 */
export const WORK_ITEM_LINKED_FIELD_KEYS: ReadonlySet<string> = new Set([
  'job_number', 'customer_name', 'customer_contact', 'customer_email',
  'site_address', 'site_gps_lat', 'site_gps_lng', 'title', 'description',
  'priority', 'status', 'scheduled_start', 'scheduled_end', 'due_date',
  'location_name', 'contact_location_id', 'location_id', 'site_id', 'site_name',
  'technician_name', 'user_name', 'inspector_name', 'completed_by', 'work_done_by',
  'contractor_name', 'inspection_done_by', 'inspected_by', 'performed_by', 'completed_by_name',
  'user_id', 'technician_id', 'inspector_id', 'contractor_id',
  'user_email', 'technician_email', 'contractor_email',
  'inspection_date', 'completion_date', 'current_date', 'date', 'inspected_date', 'performed_date',
  'work_item_id', 'work_item_number', 'appointment_number', 'appointment_title',
  'contact_person', 'contact_person_id',
  'client_name', 'client_contact', 'client_email', 'address', 'site_location', 'gps_lat', 'gps_lng',
]);

/**
 * Build prefill values for an operational form from the work item context.
 * Maps designer "Work Item" preset field keys (job_number, site_name,
 * technician_name, …) plus metadata/custom_fields key matches onto field
 * values. Pure — usable outside the hook (e.g. WorkBreakdownTab's built-in
 * modal which only has a workItemId).
 */
export function buildWorkItemPrefillValues(
  workItem: WorkItem | null | undefined,
  form: OperationalForm | null | undefined,
): Record<string, unknown> {
  if (!workItem || !form?.form_schema?.sections) return {};

  const values: Record<string, unknown> = {};
  const metadata = workItem.metadata || {};
  const customFields = workItem.custom_fields || {};

  // Current user context
  const currentUser = authService.getUser();
  const userFullName = currentUser
    ? (currentUser.display_name
        || `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim()
        || undefined)
    : undefined;
  const today = new Date().toISOString().split('T')[0];

  // Resolved context — enriched fields first (the work-items endpoint already
  // resolves cl_* from contact_locations), then customer-location and raw
  // field fallbacks.
  const coords = parseWorkItemCoordinates(workItem.location_coordinates);
  const gpsLat = workItem.location_lat ?? coords.lat;
  const gpsLng = workItem.location_lng ?? coords.lng;
  const siteName = workItem.location_name || workItem.cl_location_name;
  const siteAddress = workItem.location_address || workItem.cl_address || workItem.location;
  const customerName = workItem.customer_name || workItem.contact_name;
  const customerPhone = workItem.contact_phone || workItem.customer_phone || workItem.cl_contact_phone;
  const customerEmail = workItem.contact_email || workItem.customer_email;
  const contactPerson = workItem.contact_person || workItem.cl_contact_person;
  // "Job number" = the parent job's number on appointments/follow-ups,
  // else the item's own number.
  // parent_job_number carries the real job number on appointments/follow-ups;
  // item_number is the item's own reference (e.g. APP-0042 / JOB-0007).
  const jobNumber = workItem.parent_job_number || workItem.item_number;

  // Direct field key → work item / user / date property mappings
  const directMappings: Record<string, unknown> = {
    'job_number': jobNumber,
    'customer_name': customerName,
    'customer_contact': customerPhone,
    'customer_email': customerEmail,
    'site_address': siteAddress,
    'site_gps_lat': gpsLat,
    'site_gps_lng': gpsLng,
    'title': workItem.title,
    'description': workItem.description,
    'priority': workItem.priority,
    'status': workItem.status,
    'scheduled_start': workItem.scheduled_start,
    'scheduled_end': workItem.scheduled_end,
    'due_date': workItem.due_date,
    'location_name': siteName,
    // Site / location context
    'contact_location_id': workItem.location_id,
    'location_id': workItem.location_id,
    'site_id': workItem.location_id,
    'site_name': siteName,
    // User / technician context
    'technician_name': userFullName,
    'user_name': userFullName,
    'inspector_name': userFullName,
    'completed_by': userFullName,
    'work_done_by': userFullName,
    // Common aliases for user / technician / contractor fields
    'contractor_name': userFullName,
    'inspection_done_by': userFullName,
    'inspected_by': userFullName,
    'performed_by': userFullName,
    'completed_by_name': userFullName,
    'user_id': currentUser?.id,
    'technician_id': currentUser?.id,
    'inspector_id': currentUser?.id,
    'contractor_id': currentUser?.id,
    'user_email': currentUser?.email,
    'technician_email': currentUser?.email,
    'contractor_email': currentUser?.email,
    // Date context
    'inspection_date': today,
    'completion_date': today,
    'current_date': today,
    'date': today,
    'inspected_date': today,
    'performed_date': today,
    // Work item identifiers
    'work_item_id': workItem.id,
    'work_item_number': workItem.item_number,
    'appointment_number': workItem.item_number,
    'appointment_title': workItem.title,
    // Contact person
    'contact_person': contactPerson,
    'contact_person_id': workItem.contact_person_id,
    // Common aliases for customer / client / site fields
    'client_name': customerName,
    'client_contact': customerPhone,
    'client_email': customerEmail,
    'address': siteAddress,
    'site_location': siteName,
    'gps_lat': gpsLat,
    'gps_lng': gpsLng,
  };

  for (const section of form.form_schema.sections) {
    const sectionValues: Record<string, unknown> = {};
    for (const field of section.fields) {
      const key = field.key;
      if (directMappings[key] !== undefined) {
        sectionValues[key] = directMappings[key];
      } else if (metadata[key] !== undefined) {
        sectionValues[key] = metadata[key];
      } else if (customFields[key] !== undefined) {
        sectionValues[key] = customFields[key];
      }
    }

    if (section.is_repeatable) {
      // Repeatable sections store instance records under formData[section.key]
      // — seed each minimum instance with the resolved values so linked fields
      // land inside every instance, not as a stray top-level key.
      if (Object.keys(sectionValues).length > 0) {
        const count = section.fixed_instances
          ?? section.min_instances
          ?? section.min_repeats
          ?? 1;
        values[section.key] = Array.from({ length: count }, () => ({ ...sectionValues }));
      }
    } else {
      Object.assign(values, sectionValues);
    }
  }

  return values;
}

function getWorkItemSubtype(workItem: WorkItem | null | undefined): string | null {
  const rawSubtype = workItem?.item_subtype
    || workItem?.metadata?.subtype
    || workItem?.metadata?.item_subtype
    || workItem?.custom_fields?.subtype;

  return typeof rawSubtype === 'string' && rawSubtype.trim().length > 0
    ? rawSubtype.trim()
    : null;
}

function matchesOperationalFormTarget(form: OperationalForm, workItem: WorkItem | null | undefined): boolean {
  if (!workItem) {
    return false;
  }

  const formItemType = typeof form.applies_to_item_type === 'string'
    ? form.applies_to_item_type.trim()
    : '';
  const workItemType = typeof workItem.item_type === 'string' && workItem.item_type.trim().length > 0
    ? workItem.item_type.trim()
    : 'job';

  if (formItemType && formItemType !== workItemType) {
    return false;
  }

  const formSubtype = typeof form.applies_to_subtype === 'string'
    ? form.applies_to_subtype.trim()
    : '';
  const workItemSubtype = getWorkItemSubtype(workItem) || '';

  if (formSubtype && formSubtype !== workItemSubtype) {
    return false;
  }

  return true;
}

function matchesCachedOperationalForm(
  form: OperationalForm,
  workItem: WorkItem | null | undefined,
  parentWorkItem: WorkItem | null | undefined,
): boolean {
  if (!workItem) {
    return false;
  }

  const assignmentSource = typeof form.assignment_source === 'string'
    ? form.assignment_source.trim()
    : '';
  const assignmentSourceWorkItemId = form.assignment_source_work_item_id != null
    ? String(form.assignment_source_work_item_id)
    : null;
  const generatedForWorkItemId = form.generated_for_work_item_id != null
    ? String(form.generated_for_work_item_id)
    : null;
  const workItemId = String(workItem.id);
  const parentWorkItemId = parentWorkItem?.id != null ? String(parentWorkItem.id) : null;

  if (form.assignment_scope === 'work_item_once_off') {
    return generatedForWorkItemId === workItemId;
  }

  if (assignmentSource === 'inherited') {
    return workItem.item_type === 'appointment'
      && Boolean(parentWorkItemId)
      && assignmentSourceWorkItemId === parentWorkItemId;
  }

  if (assignmentSourceWorkItemId) {
    return assignmentSourceWorkItemId === workItemId;
  }

  return matchesOperationalFormTarget(form, workItem)
    || (workItem.item_type === 'appointment' && matchesOperationalFormTarget(form, parentWorkItem));
}

function createLocalSubmissionId(seed: string): number {
  let hash = 0;

  for (let index = 0; index < seed.length; index += 1) {
    hash = ((hash << 5) - hash + seed.charCodeAt(index)) | 0;
  }

  if (hash === 0) {
    hash = seed.length || 1;
  }

  return -Math.abs(hash);
}

function buildSubmissionMergeKey(submission: Pick<FormSubmission, 'form_key' | 'work_item_id'>): string {
  return `${submission.form_key}::${String(submission.work_item_id ?? '')}`;
}

function sortSubmissionsByRecentActivity(left: FormSubmission, right: FormSubmission): number {
  const leftTimestamp = Date.parse(left.submitted_at || left.updated_at || left.created_at || '');
  const rightTimestamp = Date.parse(right.submitted_at || right.updated_at || right.created_at || '');

  return (Number.isFinite(rightTimestamp) ? rightTimestamp : 0) - (Number.isFinite(leftTimestamp) ? leftTimestamp : 0);
}

function toQueuedOfflineSubmission(entry: Awaited<ReturnType<typeof getQueuedOfflineFormSubmissions>>[number]): FormSubmission {
  const overallStatus = entry.options?.status === 'draft'
    || entry.options?.status === 'pass'
    || entry.options?.status === 'fail'
    || entry.options?.status === 'conditional'
    || entry.options?.status === 'pending'
      ? entry.options.status
      : 'pending';

  return {
    id: createLocalSubmissionId(entry.id),
    form_configuration_id: entry.formConfigId,
    form_key: entry.formKey,
    work_item_id: entry.workItemId,
    entity_type: entry.entityType || 'work_item',
    entity_id: entry.entityId,
    client_id: 0,
    submitted_by: 0,
    form_data: entry.formData as Record<string, unknown>,
    section_results: entry.options?.sectionResults as FormSubmission['section_results'],
    overall_status: overallStatus,
    completion_percentage: 100,
    signature_data: entry.options?.signatureData as Record<string, unknown> | undefined,
    gps_data: entry.options?.gpsData,
    submitted_at: entry.queuedAt,
    created_at: entry.queuedAt,
    updated_at: entry.queuedAt,
    local_only: true,
    offline_source: 'queue',
    local_submission_id: entry.id,
  };
}

function mergeOperationalFormSubmissions(
  remoteSubmissions: FormSubmission[],
  localQueuedSubmissions: FormSubmission[],
): FormSubmission[] {
  const merged = new Map<string, FormSubmission>();

  localQueuedSubmissions.forEach((submission) => {
    merged.set(buildSubmissionMergeKey(submission), submission);
  });

  remoteSubmissions.forEach((submission) => {
    merged.set(buildSubmissionMergeKey(submission), submission);
  });

  return Array.from(merged.values()).sort(sortSubmissionsByRecentActivity);
}

function mergeOperationalFormsByFormKey<TForm extends {
  id: number;
  form_key: string;
  required_for_job?: boolean | null;
  required_for_appointment?: boolean | null;
}>(
  directForms: TForm[],
  inheritedForms: TForm[],
): TForm[] {
  const mergedForms = [...directForms];
  const indexByFormKey = new Map<string, number>();

  mergedForms.forEach((form, index) => {
    indexByFormKey.set(form.form_key || `id:${form.id}`, index);
  });

  inheritedForms.forEach((form) => {
    const formKey = form.form_key || `id:${form.id}`;
    const existingIndex = indexByFormKey.get(formKey);

    if (existingIndex == null) {
      indexByFormKey.set(formKey, mergedForms.length);
      mergedForms.push(form);
      return;
    }

    const existingForm = mergedForms[existingIndex];
    mergedForms[existingIndex] = {
      ...existingForm,
      ...form,
      required_for_job: Boolean(existingForm.required_for_job || form.required_for_job),
      required_for_appointment: Boolean(existingForm.required_for_appointment || form.required_for_appointment),
    };
  });

  return mergedForms;
}

// ============================================================================
// HOOK
// ============================================================================

interface UseOperationalFormsOptions {
  workItem: WorkItem | null;
  /** Parent job for appointments — when set, also loads the parent's forms */
  parentWorkItem?: WorkItem | null;
  /** Enable daily forms fetching (independent of work item) */
  enableDailyForms?: boolean;
}

export function useOperationalForms({ workItem, parentWorkItem, enableDailyForms = false }: UseOperationalFormsOptions) {
  const [forms, setForms] = useState<OperationalForm[]>([]);
  const [parentJobForms, setParentJobForms] = useState<OperationalForm[]>([]);
  const [dailyForms, setDailyForms] = useState<(OperationalForm & { todays_submission?: unknown; is_completed_today?: boolean })[]>([]);
  const [submissions, setSubmissions] = useState<FormSubmission[]>([]);
  const [parentJobSubmissions, setParentJobSubmissions] = useState<FormSubmission[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fetchedWorkItemKeyRef = useRef<string | null>(null);
  const dailyFetchedRef = useRef(false);
  const workItemFetchKey = useMemo(() => {
    if (!workItem?.id) {
      return null;
    }

    return [
      String(workItem.id),
      workItem.item_type || 'job',
      workItem.item_subtype || workItem.metadata?.subtype || workItem.metadata?.item_subtype || workItem.custom_fields?.subtype || '',
      parentWorkItem?.id != null ? String(parentWorkItem.id) : String(workItem.parent_id || ''),
    ].join('::');
  }, [parentWorkItem?.id, workItem]);

  const getHeaders = useCallback(() => {
    const token = authService.getTokens()?.access_token;
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }, []);

  const loadCachedForms = useCallback(async (): Promise<OperationalForm[]> => {
    if (!workItem?.id) {
      return [];
    }

    const cachedEntries = await listCachedOfflineFormConfigs<OperationalForm>();
    if (!cachedEntries.length) {
      return [];
    }

    const dedupedForms = new Map<string, OperationalForm>();

    cachedEntries.forEach((entry) => {
      const cachedForm = entry?.config;
      if (!cachedForm?.form_key || !matchesCachedOperationalForm(cachedForm, workItem, parentWorkItem)) {
        return;
      }

      dedupedForms.set(cachedForm.form_key, cachedForm);
    });

    return Array.from(dedupedForms.values()).sort((left, right) => {
      const sortOrderDelta = (left.sort_order ?? 0) - (right.sort_order ?? 0);
      if (sortOrderDelta !== 0) {
        return sortOrderDelta;
      }

      return String(left.title || '').localeCompare(String(right.title || ''));
    });
  }, [parentWorkItem, workItem]);

  const loadQueuedSubmissions = useCallback(async (): Promise<FormSubmission[]> => {
    if (!workItem?.id) {
      return [];
    }

    const workItemId = String(workItem.id);
    const queuedSubmissions = await getQueuedOfflineFormSubmissions();

    return queuedSubmissions
      .filter((submission) => String(submission.workItemId || '') === workItemId)
      .map(toQueuedOfflineSubmission)
      .sort(sortSubmissionsByRecentActivity);
  }, [workItem?.id]);

  // ── Fetch available operational forms for this work item ───────

  const fetchForms = useCallback(async () => {
    if (!workItem) {
      setParentJobForms([]);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const itemType = workItem.item_type || 'job';
      const subtype = getWorkItemSubtype(workItem);

      const params = new URLSearchParams({ item_type: itemType, client_only: 'true' });
      if (subtype) {
        params.append('subtype', subtype);
        params.append('job_type', subtype);
      }
      if (workItem.id) params.append('work_item_id', String(workItem.id));

      const res = await fetch(getApiUrl(`/api/v1/forms/operational?${params.toString()}`), {
        headers: getHeaders(),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || 'Failed to load forms');
      }

      let directForms: OperationalForm[] = [];
      const data = await res.json().catch(() => null);
      if (data?.success && Array.isArray(data.data)) {
        directForms = data.data;
      }

      // If this is an appointment with a parent job, also fetch inherited forms
      // from the parent via the inherited forms API
      let inheritedForms: OperationalForm[] = [];
      const parentId = parentWorkItem?.id || workItem.parent_id;
      if (parentId && workItem.item_type === 'appointment') {
        try {
          const inheritedParams = new URLSearchParams({
            parent_job_id: String(parentId),
            appointment_id: String(workItem.id),
          });
          const inheritedRes = await fetch(
            getApiUrl(`/api/v1/work-items/forms/inherited?${inheritedParams.toString()}`),
            { headers: getHeaders() }
          );
          if (inheritedRes.ok) {
            const inheritedData = await inheritedRes.json();
            if (inheritedData.success && inheritedData.data?.inherited_forms) {
              inheritedForms = inheritedData.data.inherited_forms;
            }
          }
        } catch (err) {
          if (!isSilentFetchError(err)) logger.error('[OperationalForms] Failed to fetch inherited forms:', err);
        }
      }

      let resolvedParentJobForms: OperationalForm[] = [];
      if (parentId && workItem.item_type === 'appointment') {
        try {
          const parentItemType = parentWorkItem?.item_type || 'job';
          const parentSubtype = getWorkItemSubtype(parentWorkItem);
          const parentFormParams = new URLSearchParams({
            item_type: parentItemType,
            work_item_id: String(parentId),
            client_only: 'true',
          });

          if (parentSubtype) {
            parentFormParams.append('subtype', parentSubtype);
            parentFormParams.append('job_type', parentSubtype);
          }

          const parentFormsRes = await fetch(
            getApiUrl(`/api/v1/forms/operational?${parentFormParams.toString()}`),
            { headers: getHeaders() }
          );

          if (parentFormsRes.ok) {
            const parentFormsData = await parentFormsRes.json().catch(() => null);
            if (parentFormsData?.success && Array.isArray(parentFormsData.data)) {
              resolvedParentJobForms = parentFormsData.data;
            }
          }
        } catch (err) {
          if (!isSilentFetchError(err)) logger.error('[OperationalForms] Failed to fetch parent job forms:', err);
        }
      }

      // Merge: direct forms + inherited, preserving inherited requirement flags
      // when the same form is available through both appointment targeting and
      // parent-job inheritance.
      const merged = mergeOperationalFormsByFormKey(directForms, inheritedForms);
      setForms(merged);
      setParentJobForms(resolvedParentJobForms);

      if (merged.length > 0) {
        void Promise.allSettled(
          merged
            .filter((form) => Boolean(form?.form_key))
            .map((form) => cacheOfflineFormConfig(form.form_key, form)),
        );
      }
    } catch (err) {
      if (!isSilentFetchError(err)) logger.error('[OperationalForms] Failed to fetch forms:', err);

      const cachedForms = await loadCachedForms();
      if (cachedForms.length > 0) {
        setForms(cachedForms);
        setError(null);
      } else {
        setForms([]);
        setError('Failed to load operational forms');
      }

      setParentJobForms([]);
    } finally {
      setIsLoading(false);
    }
  }, [getHeaders, loadCachedForms, parentWorkItem, workItem]);

  // ── Fetch existing submissions for this work item ─────────────

  const fetchSubmissions = useCallback(async () => {
    if (!workItem?.id) {
      setParentJobSubmissions([]);
      return;
    }

    const localQueuedSubmissions = await loadQueuedSubmissions();
    let resolvedSubmissions = localQueuedSubmissions;

    try {
      // For jobs, include_children=true to pick up submissions made by child appointments
      const includeChildren = workItem.item_type === 'job';
      const res = await fetch(
        getApiUrl(`/api/v1/forms/submissions?work_item_id=${workItem.id}${includeChildren ? '&include_children=true' : ''}`),
        { headers: getHeaders() }
      );

      if (res.ok) {
        const data = await res.json();
        if (data.success && Array.isArray(data.data)) {
          resolvedSubmissions = mergeOperationalFormSubmissions(data.data, localQueuedSubmissions);
        }
      }
    } catch (err) {
      if (!isSilentFetchError(err)) logger.error('[OperationalForms] Failed to fetch submissions:', err);
    }

    setSubmissions(resolvedSubmissions);

    const parentId = parentWorkItem?.id || workItem.parent_id;
    if (parentId && workItem.item_type === 'appointment') {
      try {
        const parentSubmissionsRes = await fetch(
          getApiUrl(`/api/v1/forms/submissions?work_item_id=${parentId}&include_children=true`),
          { headers: getHeaders() }
        );

        if (parentSubmissionsRes.ok) {
          const parentSubmissionsData = await parentSubmissionsRes.json().catch(() => null);
          if (parentSubmissionsData?.success && Array.isArray(parentSubmissionsData.data)) {
            setParentJobSubmissions(
              mergeOperationalFormSubmissions(parentSubmissionsData.data, localQueuedSubmissions)
            );
            return;
          }
        }
      } catch (err) {
        if (!isSilentFetchError(err)) logger.error('[OperationalForms] Failed to fetch parent job submissions:', err);
      }

      setParentJobSubmissions(localQueuedSubmissions);
      return;
    }

    setParentJobSubmissions([]);
  }, [getHeaders, loadQueuedSubmissions, parentWorkItem?.id, workItem]);

  // ── Fetch daily forms (independent of work item) ──────────────

  const fetchDailyForms = useCallback(async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const res = await fetch(
        getApiUrl(`/api/v1/forms/daily?date=${today}`),
        { headers: getHeaders() }
      );

      if (res.ok) {
        const data = await res.json();
        if (data.success && Array.isArray(data.data)) {
          setDailyForms(data.data);
        }
      }
    } catch (err) {
      if (!isSilentFetchError(err)) logger.error('[OperationalForms] Failed to fetch daily forms:', err);
    }
  }, [getHeaders]);

  // ── Auto-fetch on work item change ────────────────────────────

  useEffect(() => {
    if (workItemFetchKey && fetchedWorkItemKeyRef.current !== workItemFetchKey) {
      fetchedWorkItemKeyRef.current = workItemFetchKey;
      fetchForms();
      fetchSubmissions();
    }
    if (!workItemFetchKey) {
      fetchedWorkItemKeyRef.current = null;
      setForms([]);
      setSubmissions([]);
    }
  }, [fetchForms, fetchSubmissions, workItemFetchKey]);

  // ── Auto-fetch daily forms if enabled ─────────────────────────

  useEffect(() => {
    if (enableDailyForms && !dailyFetchedRef.current) {
      dailyFetchedRef.current = true;
      fetchDailyForms();
    }
  }, [enableDailyForms, fetchDailyForms]);

  useEffect(() => {
    if (!workItem?.id) return;

    const handleOfflineFormsSynced = () => {
      void fetchSubmissions();
    };

    const handlePendingOfflineFormsChanged = (event: Event) => {
      const detail = (event as CustomEvent<{ source?: string }>).detail;
      if (!detail?.source || detail.source === 'forms') {
        void fetchSubmissions();
      }
    };

    window.addEventListener('forms-offline-sync-complete', handleOfflineFormsSynced);
    window.addEventListener(OPERATIONS_SYNC_PENDING_CHANGED_EVENT, handlePendingOfflineFormsChanged as EventListener);

    return () => {
      window.removeEventListener('forms-offline-sync-complete', handleOfflineFormsSynced);
      window.removeEventListener(OPERATIONS_SYNC_PENDING_CHANGED_EVENT, handlePendingOfflineFormsChanged as EventListener);
    };
  }, [fetchSubmissions, workItem?.id]);

  // ── Pre-fill form data from work item ─────────────────────────

  const getPrefilledValues = useCallback((form: OperationalForm | null): Record<string, unknown> => {
    return buildWorkItemPrefillValues(workItem, form);
  }, [workItem]);

  // ── Submit a form ─────────────────────────────────────────────

  const submitForm = useCallback(async (
    form: OperationalForm,
    formData: Record<string, unknown>,
    options?: {
      generatePdf?: boolean;
      status?: 'draft' | 'pass' | 'fail' | 'conditional';
      sectionResults?: FormSubmission['section_results'];
      signatureData?: Record<string, unknown>;
      gpsData?: { lat: number; lng: number; accuracy: number };
      workItemIdOverride?: number | string;
      /** When set, update (PATCH) this existing submission instead of creating a new one. */
      existingSubmissionId?: number;
    }
  ): Promise<FormSubmission | null> => {
    if (!workItem?.id) return null;

    try {
      setIsSubmitting(true);
      setError(null);
      const targetWorkItemId = options?.workItemIdOverride ?? workItem.id;

      // Editing an existing saved submission → PATCH it instead of creating a duplicate
      if (options?.existingSubmissionId) {
        const patchRes = await fetch(getApiUrl(`/api/v1/forms/submissions/${options.existingSubmissionId}`), {
          method: 'PATCH',
          headers: getHeaders(),
          body: JSON.stringify({
            form_data: formData,
            overall_status: options?.status,
            section_results: options?.sectionResults,
            signature_data: options?.signatureData,
            gps_data: options?.gpsData,
          }),
        });

        const patchData = await patchRes.json().catch(() => ({}));
        if (patchRes.ok && patchData?.success && patchData?.data) {
          const submission: FormSubmission = patchData.data;
          setSubmissions(prev => prev.map(s => (s.id === submission.id ? submission : s)));
          return submission;
        }
        // Throw so callers (OperationalFormModal) can display the server's real
        // reason — e.g. FORM_EDIT_WINDOW_CLOSED after the editable window ends.
        const patchErrorMessage = patchData?.error || `Form update failed (HTTP ${patchRes.status})`;
        setError(patchErrorMessage);
        throw new Error(patchErrorMessage);
      }

      const res = await fetch(getApiUrl('/api/v1/forms/submissions'), {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          form_configuration_id: form.id,
          form_key: form.form_key,
          work_item_id: targetWorkItemId,
          entity_type: form.entity_type,
          form_data: formData,
          overall_status: options?.status,
          section_results: options?.sectionResults,
          signature_data: options?.signatureData,
          gps_data: options?.gpsData,
          generate_pdf: options?.generatePdf ?? false,
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (res.ok && data?.success && data?.data) {
        const submission: FormSubmission = data.data;
        setSubmissions(prev => [...prev, submission]);
        return submission;
      }
      // Throw so callers surface the server's real reason instead of a generic
      // "submission failed" — e.g. FORM_EDIT_WINDOW_CLOSED after the editable
      // window ends.
      const errorMessage = data?.error || `Form submission failed (HTTP ${res.status})`;
      setError(errorMessage);
      throw new Error(errorMessage);
    } catch (err) {
      if (err instanceof Error) {
        if (!isSilentFetchError(err)) logger.error('[OperationalForms] Failed to submit form:', err);
        throw err;
      }
      if (!isSilentFetchError(err)) logger.error('[OperationalForms] Failed to submit form:', err);
      setError('Network error during form submission');
      throw new Error('Network error during form submission');
    } finally {
      setIsSubmitting(false);
    }
  }, [workItem?.id, getHeaders]);

  // ── Compute section status from form data ─────────────────────

  const computeSectionResults = useCallback((
    form: OperationalForm,
    formData: Record<string, unknown>
  ): FormSubmission['section_results'] => {
    return form.form_schema.sections.map(section => {
      const fields = section.fields || [];
      let completed = 0;
      let status: 'pass' | 'fail' | 'na' | 'pending' = 'pending';

      for (const field of fields) {
        const val = formData[field.key];
        if (val !== undefined && val !== null && val !== '') {
          completed++;

          // Check pass/fail fields
          if (field.type === 'pass_fail') {
            const result = typeof val === 'object' && val !== null ? (val as Record<string, unknown>).result : val;
            if (result === 'fail') {
              status = 'fail';
            }
          }
          // Check measurement fields against bounds (validation canonical, options legacy fallback)
          if (field.type === 'measurement' && typeof val === 'number') {
            const legacyOptions = (field.options && typeof field.options === 'object' && !Array.isArray(field.options))
              ? field.options as Record<string, unknown>
              : {};
            const minVal = toNumberOrUndefined(field.validation?.min_value)
              ?? toNumberOrUndefined(field.validation?.min)
              ?? toNumberOrUndefined(legacyOptions.min);
            const maxVal = toNumberOrUndefined(field.validation?.max_value)
              ?? toNumberOrUndefined(field.validation?.max)
              ?? toNumberOrUndefined(legacyOptions.max);
            if ((minVal !== undefined && val < minVal) || (maxVal !== undefined && val > maxVal)) {
              status = 'fail';
            }
          }
        }
      }

      // If no failures detected and something completed, it's a pass
      if (status !== 'fail' && completed > 0) {
        status = completed >= fields.filter(f => f.required).length ? 'pass' : 'pending';
      }

      return {
        section_key: section.key,
        status,
        fields_completed: completed,
        fields_total: fields.length,
      };
    });
  }, []);

  // ── Compute overall form status ───────────────────────────────

  const computeOverallStatus = useCallback((
    sectionResults: FormSubmission['section_results']
  ): 'pass' | 'fail' | 'conditional' | 'pending' => {
    if (!sectionResults?.length) return 'pending';

    const statuses = sectionResults.map(s => s.status);
    if (statuses.some(s => s === 'fail')) return 'fail';
    if (statuses.every(s => s === 'pass' || s === 'na')) return 'pass';
    if (statuses.some(s => s === 'pass')) return 'conditional';
    return 'pending';
  }, []);

  // ── Job closing gate ──────────────────────────────────────────

  /** Forms that still require completion before the job can close */
  const incompleteForms = useMemo(() => {
    if (!forms.length) return [];
    return getIncompleteRequiredOperationalForms(forms, submissions);
  }, [forms, submissions]);

  const incompleteJobForms = useMemo(() => {
    if (!forms.length) return [];
    return getIncompleteRequiredOperationalForms(forms, submissions, { scope: 'job' });
  }, [forms, submissions]);

  const incompleteAppointmentForms = useMemo(() => {
    if (!forms.length) return [];
    return getIncompleteRequiredOperationalForms(forms, submissions, { scope: 'appointment' });
  }, [forms, submissions]);

  const incompleteParentJobForms = useMemo(() => {
    if (workItem?.item_type !== 'appointment') {
      return [];
    }

    const formsToEvaluate = parentJobForms.length > 0 ? parentJobForms : forms;
    const submissionsToEvaluate = parentJobForms.length > 0 ? parentJobSubmissions : submissions;

    if (!formsToEvaluate.length) {
      return [];
    }

    return getIncompleteRequiredOperationalForms(formsToEvaluate, submissionsToEvaluate, { scope: 'job' });
  }, [forms, parentJobForms, parentJobSubmissions, submissions, workItem?.item_type]);

  /** Whether all required forms are completed — safe to close job */
  const canCloseJob = useMemo(() => {
    return incompleteJobForms.length === 0;
  }, [incompleteJobForms]);

  /** Get submission for a specific form */
  const getSubmission = useCallback((formKey: string): FormSubmission | undefined => {
    return submissions.find(s => s.form_key === formKey);
  }, [submissions]);

  /** Check if a specific form has been submitted */
  const isFormSubmitted = useCallback((formKey: string): boolean => {
    return submissions.some(s => s.form_key === formKey);
  }, [submissions]);

  return {
    // State
    forms,
    parentJobForms,
    dailyForms,
    submissions,
    parentJobSubmissions,
    isLoading,
    isSubmitting,
    error,

    // Actions
    fetchForms,
    fetchDailyForms,
    fetchSubmissions,
    submitForm,
    getPrefilledValues,
    computeSectionResults,
    computeOverallStatus,

    // Helpers
    getSubmission,
    isFormSubmitted,

    // Job closing gate
    incompleteForms,
    incompleteJobForms,
    incompleteAppointmentForms,
    incompleteParentJobForms,
    canCloseJob,
  };
}

export type UseOperationalFormsReturn = ReturnType<typeof useOperationalForms>;
